const SERVICE_TYPES = [
  'plumbing',
  'roofing',
  'electrical',
  'siding',
  'HVAC',
  'painting',
  'masonry',
  'concrete',
  'locksmith',
  'landscaping',
  'snow removal',
  'inside cleaning',
  'trash pickup',
  'appliances',
  'appliance repair',
  'other',
];

function serviceTypeLabel(type) {
  if (type === 'HVAC') return 'HVAC';
  const text = String(type || '').trim();
  if (!text) return '';
  return text
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const SERVICE_TYPE_CHIPS = {
  plumbing: { background: '#e3f2fd', color: '#0d47a1' },
  roofing: { background: '#ffe0b2', color: '#e65100' },
  electrical: { background: '#fff8e1', color: '#f57f17' },
  siding: { background: '#f3e5f5', color: '#6a1b9a' },
  HVAC: { background: '#e0f7fa', color: '#006064' },
  painting: { background: '#fce4ec', color: '#ad1457' },
  masonry: { background: '#efebe9', color: '#4e342e' },
  concrete: { background: '#eceff1', color: '#37474f' },
  locksmith: { background: '#e8eaf6', color: '#283593' },
  landscaping: { background: '#e8f5e9', color: '#1b5e20' },
  'snow removal': { background: '#e1f5fe', color: '#01579b' },
  'inside cleaning': { background: '#e0f2f1', color: '#004d40' },
  'trash pickup': { background: '#f0f4c3', color: '#827717' },
  appliances: { background: '#fbe9e7', color: '#bf360c' },
  'appliance repair': { background: '#ede7f6', color: '#4527a0' },
  other: { background: '#eeeeee', color: '#424242' },
};

const DEFAULT_TYPE_CHIP = { background: '#eeeeee', color: '#424242' };

function serviceTypeChipStyle(type) {
  const chip = SERVICE_TYPE_CHIPS[type] || DEFAULT_TYPE_CHIP;
  return { background: chip.background, color: chip.color };
}

function phoneDigits(value) {
  const digits = String(value || '').replace(/\D/g, '');
  const ten =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return ten.length === 10 ? ten : '';
}

function phoneDisplay(value) {
  const ten = phoneDigits(value);
  if (!ten) return '';
  return `${ten.slice(0, 3)}-${ten.slice(3, 6)}-${ten.slice(6)}`;
}

function canonicalPhone(value) {
  const shown = phoneDisplay(value);
  if (shown) return shown;
  return String(value ?? '').trim();
}

function phoneTelHref(value) {
  const ten = phoneDigits(value);
  return ten ? `tel:${ten}` : '';
}

function websiteUrl(value) {
  const text = String(value || '')
    .trim()
    .replace(/[),.;]+$/, '');
  if (!text || /^(?:javascript|data|vbscript):/i.test(text)) return '';
  if (/^https?:\/\//i.test(text)) return text;
  if (/^[\w.-]+\.[a-z]{2,}(?:[/?#][^\s]*)?$/i.test(text)) {
    return `https://${text}`;
  }
  return '';
}

function websiteHostLabel(href) {
  try {
    const host = new URL(href).hostname.replace(/^www\./i, '');
    return host || href;
  } catch (err) {
    return href;
  }
}

function cleanWebsiteLabel(value) {
  return String(value || '')
    .replace(/[\[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function serviceWebsiteLinks(value) {
  const text = String(value ?? '').trim();
  if (!text) return [];
  const links = [];
  const re = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let match;
  while ((match = re.exec(text))) {
    pushBareWebsiteLinks(links, text.slice(last, match.index));
    const href = websiteUrl(match[2]);
    const label = cleanWebsiteLabel(match[1]);
    if (href && label) links.push({ label, href });
    last = match.index + match[0].length;
  }
  pushBareWebsiteLinks(links, text.slice(last));
  return links;
}

function pushBareWebsiteLinks(links, chunk) {
  const tokens = String(chunk || '').split(/\s+/).filter(Boolean);
  for (const token of tokens) {
    const href = websiteUrl(token);
    if (!href) continue;
    links.push({ label: websiteHostLabel(href), href });
  }
}

function formatServiceWebsite(value) {
  const text = String(value ?? '').trim();
  const links = serviceWebsiteLinks(text);
  if (!links.length) {
    if (/(?:javascript|data|vbscript)\s*:/i.test(text)) return '';
    return text.length > 255 ? text.slice(0, 255) : text;
  }
  let out = '';
  for (const link of links) {
    const line = `[${cleanWebsiteLabel(link.label)}](${link.href})`;
    const next = out ? `${out}\n${line}` : line;
    if (next.length > 255) break;
    out = next;
  }
  if (out) return out;
  const bare = links[0].href;
  return bare.length > 255 ? bare.slice(0, 255) : bare;
}

function websiteHref(value) {
  const links = serviceWebsiteLinks(value);
  return links.length ? links[0].href : '';
}

const SERVICE_CONTACT_DRAFT_KEY = 'pixie-service-contact-draft';

const DRAFT_FIELDS = [
  'businessName',
  'serviceType',
  'website',
  'email',
  'businessPhone',
  'primaryContactName',
  'primaryContactPhone',
  'notes',
];

function serviceContactDraft(value) {
  const source = value && typeof value === 'object' ? value : {};
  const id = Number(source.id);
  const draft = {
    id: Number.isInteger(id) && id > 0 ? id : null,
  };
  for (const key of DRAFT_FIELDS) {
    draft[key] = source[key] == null ? '' : String(source[key]);
  }
  if (!draft.serviceType) draft.serviceType = 'plumbing';
  return draft;
}

function readServiceContactDraft(storage) {
  try {
    if (!storage || typeof storage.getItem !== 'function') return null;
    const raw = storage.getItem(SERVICE_CONTACT_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return serviceContactDraft(parsed);
  } catch (err) {
    return null;
  }
}

function writeServiceContactDraft(draft, storage) {
  try {
    if (!storage || typeof storage.setItem !== 'function') return;
    storage.setItem(
      SERVICE_CONTACT_DRAFT_KEY,
      JSON.stringify(serviceContactDraft(draft)),
    );
  } catch (err) {
    /* quota or private mode */
  }
}

function clearServiceContactDraft(storage) {
  try {
    if (!storage || typeof storage.removeItem !== 'function') return;
    storage.removeItem(SERVICE_CONTACT_DRAFT_KEY);
  } catch (err) {
    /* private mode */
  }
}

function filterServiceContacts(rows, query, type) {
  const wanted = String(type || '').trim();
  const q = String(query || '').trim().toLowerCase();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!row) return false;
    if (wanted && row.serviceType !== wanted) return false;
    if (!q) return true;
    const haystack = [
      row.businessName,
      row.serviceType,
      serviceTypeLabel(row.serviceType),
      row.website,
      row.email,
      row.businessPhone,
      phoneDisplay(row.businessPhone),
      row.primaryContactName,
      row.primaryContactPhone,
      phoneDisplay(row.primaryContactPhone),
      row.notes,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

module.exports = {
  SERVICE_TYPES,
  SERVICE_CONTACT_DRAFT_KEY,
  serviceTypeLabel,
  serviceTypeChipStyle,
  phoneDigits,
  phoneDisplay,
  canonicalPhone,
  phoneTelHref,
  websiteHref,
  serviceWebsiteLinks,
  formatServiceWebsite,
  filterServiceContacts,
  serviceContactDraft,
  readServiceContactDraft,
  writeServiceContactDraft,
  clearServiceContactDraft,
};
